import {
  OrderStatus,
  OrderItemStatus,
  PaymentStatus,
  DTCMOrderStatus,
  Payment,
} from "client";
import { getOrderById } from "services/order";

export enum OrderVirtualStatus {
  NEW = "NEW",
  ON_GOING = "ON_GOING",
  COMPLETED = "COMPLETED",
  CANCELED = "CANCELED",
  EXPIRED = "EXPIRED",
  DELETED = "DELETED",
  PARTIALLY_APPROVED = "PARTIALLY_APPROVED",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  REQUEST_REVISION = "REQUEST_REVISION",
  PENDING_APPROVAL = "PENDING_APPROVAL",
  AWAITING_PAYMENT = "AWAITING_PAYMENT",
  PARTIALLY_PAID = "PARTIALLY_PAID",
  PAID = "PAID",
  FAILED = "FAILED",
  DTCM_COMPLETED = "DTCM_COMPLETED",
  DTCM_PENDING = "DTCM_PENDING",
}

const isFinalStatus = (status: OrderStatus | OrderVirtualStatus) =>
  [
    OrderVirtualStatus.CANCELED,
    OrderVirtualStatus.EXPIRED,
    OrderVirtualStatus.DELETED,
    OrderVirtualStatus.ON_GOING,
  ].includes(status as OrderVirtualStatus);

const hasPendingApproval = (items: any[]) =>
  items.some((item) => item.status === OrderItemStatus.PENDING_APPROVAL);

const isPartiallyApproved = (items: any[]) =>
  items.some((item) =>
    [OrderItemStatus.APPROVED, OrderItemStatus.PENDING_APPROVAL].includes(
      item.status
    )
  );
const areAllItemsRejected = (items: any[]) =>
  items.length > 0 &&
  items.every((item) => item.status === OrderItemStatus.REJECTED);
const areAllItemsApproved = (items: any[]) =>
  items.length > 0 &&
  items.every((item) => item.status === OrderItemStatus.APPROVED);
const areAllItemsHasRequestRevision = (items: any[]) =>
  items.length > 0 &&
  items.every((item) => item.status === OrderItemStatus.REQUEST_REVISION);
const areAllItemsConfirmed = (items: any[]) =>
  items.length > 0 &&
  items.every((item) => item.status === OrderItemStatus.CONFIRMED);

// FAILED / EXPIRED payments are abandoned attempts (e.g. a declined card or an
// offline bill that timed out). When a customer retries, a fresh payment record
// is created, so these superseded attempts must be ignored when deriving whether
// the order is settled. Otherwise a successful retry would leave the order stuck
// (e.g. [FAILED, PAID] would never be considered fully paid).
//
// Exported so the MongoDB reporting aggregations (orderReport, ticket) reuse the
// exact same definition and stay in sync with `calculateOrderStatus`.
export const SUPERSEDED_PAYMENT_STATUSES: PaymentStatus[] = [
  PaymentStatus.FAILED,
  PaymentStatus.EXPIRED,
];

const isSupersededPayment = (status: PaymentStatus) =>
  SUPERSEDED_PAYMENT_STATUSES.includes(status);

const getActivePayments = (payments: Payment[]) =>
  payments.filter((p) => !isSupersededPayment(p.status));

const areAllPaymentsPaid = (payments: Payment[]) => {
  // No payment records at all => nothing to settle (e.g. free orders).
  if (payments.length === 0) return true;

  const activePayments = getActivePayments(payments);
  // Every attempt failed/expired with no successful retry yet => not settled.
  return (
    activePayments.length > 0 &&
    activePayments.every((p) => p.status === PaymentStatus.PAID)
  );
};

const isAnyPaymentPaid = (payments: Payment[]) =>
  payments.some((p) => p.status === PaymentStatus.PAID);

const areAllPaymentsUnpaid = (payments: Payment[]) => {
  const activePayments = getActivePayments(payments);
  return (
    activePayments.length > 0 &&
    activePayments.every((p) => p.status !== PaymentStatus.PAID)
  );
};

// When every payment attempt is superseded (FAILED/EXPIRED) and there is no
// active attempt left (nothing AWAITING or PAID), the order never settled and no
// retry is in flight. Surface that terminal outcome instead of falling through
// to ON_GOING: EXPIRED only when every attempt expired, otherwise FAILED (a
// declined/failed attempt is the more actionable signal).
const getSupersededPaymentStatus = (
  payments: Payment[]
): OrderVirtualStatus.FAILED | OrderVirtualStatus.EXPIRED | null => {
  if (payments.length === 0) return null;
  if (getActivePayments(payments).length > 0) return null;

  const allExpired = payments.every((p) => p.status === PaymentStatus.EXPIRED);
  return allExpired ? OrderVirtualStatus.EXPIRED : OrderVirtualStatus.FAILED;
};

/**
 * Builds the MongoDB aggregation expressions that mirror the runtime
 * `areAllPaymentsPaid` / `isAnyPaymentPaid` / `areAllPaymentsUnpaid` helpers.
 *
 * Reporting pipelines (orderReport, ticket) derive `virtualStatus` from a
 * flattened array of payment status strings. Spreading the result into an
 * `$addFields` stage keeps the report's paid / partially-paid / unpaid logic
 * identical to `calculateOrderStatus`, including ignoring FAILED/EXPIRED retry
 * attempts so a `[FAILED, PAID]` order is reported as PAID/COMPLETED.
 *
 * @param paymentStatusesField Field path holding the flattened array of payment
 * status strings (defaults to "$paymentStatuses").
 */
export const buildPaymentSettlementExpressions = (
  paymentStatusesField = "$paymentStatuses"
) => {
  const activePaymentStatuses = {
    $filter: {
      input: paymentStatusesField,
      as: "status",
      cond: { $not: { $in: ["$$status", SUPERSEDED_PAYMENT_STATUSES] } },
    },
  };

  return {
    allPaid: {
      $let: {
        vars: { activePaymentStatuses },
        in: {
          $or: [
            // No payment records at all => nothing to settle (free orders).
            { $eq: [{ $size: paymentStatusesField }, 0] },
            // Ignoring failed/expired attempts, every remaining attempt is PAID.
            {
              $and: [
                { $gt: [{ $size: "$$activePaymentStatuses" }, 0] },
                {
                  $allElementsTrue: {
                    $map: {
                      input: "$$activePaymentStatuses",
                      as: "status",
                      in: { $eq: ["$$status", PaymentStatus.PAID] },
                    },
                  },
                },
              ],
            },
          ],
        },
      },
    },
    anyPaid: { $in: [PaymentStatus.PAID, paymentStatusesField] },
    allUnpaid: {
      $let: {
        vars: { activePaymentStatuses },
        in: {
          $and: [
            { $gt: [{ $size: "$$activePaymentStatuses" }, 0] },
            { $not: { $in: [PaymentStatus.PAID, "$$activePaymentStatuses"] } },
          ],
        },
      },
    },
    hasActiveAwaitingPayment: {
      $let: {
        vars: { activePaymentStatuses },
        in: {
          $in: [PaymentStatus.AWAITING_PAYMENT, "$$activePaymentStatuses"],
        },
      },
    },
    // There is at least one payment attempt, but every attempt is superseded
    // (FAILED/EXPIRED) with nothing AWAITING or PAID left.
    hasOnlySupersededPayments: {
      $let: {
        vars: { activePaymentStatuses },
        in: {
          $and: [
            { $gt: [{ $size: paymentStatusesField }, 0] },
            { $eq: [{ $size: "$$activePaymentStatuses" }, 0] },
          ],
        },
      },
    },
    // EXPIRED only when every attempt expired, otherwise FAILED. Consumers must
    // guard this with `hasOnlySupersededPayments` (an empty array is all-expired
    // vacuously true, but then there is nothing to supersede).
    supersededPaymentStatus: {
      $cond: {
        if: {
          $allElementsTrue: {
            $map: {
              input: paymentStatusesField,
              as: "status",
              in: { $eq: ["$$status", PaymentStatus.EXPIRED] },
            },
          },
        },
        then: OrderVirtualStatus.EXPIRED,
        else: OrderVirtualStatus.FAILED,
      },
    },
  };
};

export const calculateOrderStatus = async (
  orderId: string
): Promise<OrderVirtualStatus> => {
  try {
    const order = await getOrderById(orderId);
    if (!order) throw new Error("Order not found");
    const { status, orderItems = [], payments = [], dtcmOrder } = order;

    if (isFinalStatus(status)) {
      // const logData = [
      //   {
      //     Variable: "orderId",
      //     Value: orderId,
      //   },
      //   {
      //     Variable: "order.status",
      //     Value: status,
      //   },
      //   {
      //     Variable: "isFinalStatus",
      //     Value: true,
      //   },
      //   {
      //     Variable: "Return Value",
      //     Value: status,
      //   },
      // ];
      // console.table(logData);
      return status as OrderVirtualStatus;
    }

    const allConfirmed = areAllItemsConfirmed(orderItems);
    const allPaid = areAllPaymentsPaid(payments);
    const anyPaid = isAnyPaymentPaid(payments);
    const allUnpaid = areAllPaymentsUnpaid(payments);
    const supersededPaymentStatus = getSupersededPaymentStatus(payments);
    const allRejected = areAllItemsRejected(orderItems);
    const allRequestRevision = areAllItemsHasRequestRevision(orderItems);
    const allApproved = areAllItemsApproved(orderItems);
    const hasPending = hasPendingApproval(orderItems);
    const partiallyApproved = isPartiallyApproved(orderItems);

    // Log all variables before return
    // const logData = [
    //   {
    //     Variable: "orderId",
    //     Value: orderId,
    //   },
    //   {
    //     Variable: "order.status",
    //     Value: status,
    //   },
    //   {
    //     Variable: "orderItems.length",
    //     Value: orderItems.length,
    //   },
    //   {
    //     Variable: "payments.length",
    //     Value: payments.length,
    //   },
    //   {
    //     Variable: "dtcmOrder?.status",
    //     Value: dtcmOrder?.status ?? "null/undefined",
    //   },
    //   {
    //     Variable: "allConfirmed",
    //     Value: allConfirmed,
    //   },
    //   {
    //     Variable: "allPaid",
    //     Value: allPaid,
    //   },
    //   {
    //     Variable: "anyPaid",
    //     Value: anyPaid,
    //   },
    //   {
    //     Variable: "allUnpaid",
    //     Value: allUnpaid,
    //   },
    //   {
    //     Variable: "allRejected",
    //     Value: allRejected,
    //   },
    //   {
    //     Variable: "allRequestRevision",
    //     Value: allRequestRevision,
    //   },
    //   {
    //     Variable: "allApproved",
    //     Value: allApproved,
    //   },
    //   {
    //     Variable: "hasPendingApproval",
    //     Value: hasPending,
    //   },
    //   {
    //     Variable: "isPartiallyApproved",
    //     Value: partiallyApproved,
    //   },
    // ];

    let returnValue: OrderVirtualStatus;

    if (allConfirmed && allPaid) {
      if (dtcmOrder?.status === DTCMOrderStatus.COMPLETED) {
        returnValue = OrderVirtualStatus.DTCM_COMPLETED;
      } else if (
        dtcmOrder?.status === DTCMOrderStatus.PENDING ||
        dtcmOrder?.status === DTCMOrderStatus.DRAFT
      ) {
        returnValue = OrderVirtualStatus.DTCM_PENDING;
      } else {
        returnValue = OrderVirtualStatus.COMPLETED;
      }
    } else if (allConfirmed && anyPaid) {
      returnValue = OrderVirtualStatus.PARTIALLY_PAID;
    } else if (allRejected) {
      returnValue = OrderVirtualStatus.REJECTED;
    } else if (allRequestRevision) {
      returnValue = OrderVirtualStatus.REQUEST_REVISION;
    } else if (allApproved) {
      returnValue = OrderVirtualStatus.APPROVED;
    } else if (hasPending) {
      returnValue = OrderVirtualStatus.PENDING_APPROVAL;
    } else if (partiallyApproved) {
      returnValue = OrderVirtualStatus.PARTIALLY_APPROVED;
    } else if (allPaid) {
      returnValue = OrderVirtualStatus.PAID;
    } else if (anyPaid) {
      returnValue = OrderVirtualStatus.PARTIALLY_PAID;
    } else if (allUnpaid) {
      returnValue = OrderVirtualStatus.AWAITING_PAYMENT;
    } else if (supersededPaymentStatus) {
      // Only FAILED/EXPIRED attempts remain and none is awaiting a retry.
      returnValue = supersededPaymentStatus;
    } else {
      returnValue = OrderVirtualStatus.ON_GOING;
    }

    // Add return value to log data
    // logData.push({
    //   Variable: "Return Value",
    //   Value: returnValue,
    // });

    // console.table(logData);
    return returnValue;
  } catch (error) {
    console.error("Error calculating order status:", error);
    throw error;
  }
};
